import {
  PimProductPreferenceScope,
  PimSupplierProductArticleNumberInput,
} from "../../../clients/graphqlTypes.js";
import {
  AddProductPreferenceResultFragment,
  ProductPreferenceFragment,
} from "./ProductPreferences.generated.js";

/**
 * The single product type a preference can be keyed to, or `null` when the filters leave it
 * ambiguous. A preference is stored per `(article, product type, scope)`, so inferring a
 * product type from anything other than an unambiguous filter would silently write a
 * preference the user never asked for.
 */
export function getProductTypeInContext(
  filters: { productTypeIds?: string[] | null } | null | undefined
): string | null {
  const [only, ...rest] = filters?.productTypeIds ?? [];
  return rest.length === 0 && only !== undefined ? only : null;
}

/**
 * The product type a preference written from the product search is keyed to.
 *
 * A pinned type wins over the filtered one: where the caller opened the search to work on one
 * product type, widening or clearing the product type filter is how the user reaches articles
 * the catalogue has not classified as it yet — so the filter must be free to move without
 * moving what the preference is written against.
 */
export function getPreferenceProductType(
  pinnedProductTypeId: string | null,
  filters: { productTypeIds?: string[] | null } | null | undefined
): string | null {
  return pinnedProductTypeId ?? getProductTypeInContext(filters);
}

export function articleNumberIdentifierKey(identifier: {
  supplierId: string;
  articleNumber: string;
}): string {
  return `${identifier.supplierId}/${identifier.articleNumber}`;
}

export type ProductPreferenceScopes = {
  user: boolean;
  organisation: boolean;
};

export const NO_PREFERENCE: ProductPreferenceScopes = {
  user: false,
  organisation: false,
};

/**
 * Preferred state per article, keyed by `(supplierId, articleNumber)`. A hit's `preferences`
 * attribution is keyed to the search's context type and carries no scope, so the UI derives
 * the toggle state by matching the preference list instead.
 */
export function indexPreferencesByArticle(
  preferences: readonly ProductPreferenceFragment[]
): Map<string, ProductPreferenceScopes> {
  const byArticle = new Map<string, ProductPreferenceScopes>();

  for (const preference of preferences) {
    const key = articleNumberIdentifierKey(preference.articleNumberIdentifier);
    const current = byArticle.get(key) ?? { ...NO_PREFERENCE };
    byArticle.set(key, {
      user: current.user || preference.scope === "USER",
      organisation: current.organisation || preference.scope === "ORGANISATION",
    });
  }

  return byArticle;
}

export type AddProductPreferenceSummary = {
  added: PimSupplierProductArticleNumberInput[];
  conflicts: {
    articleNumber: string;
    supplierId: string;
    productTypeKey: string;
    /** The label when PIM resolves the product type, else its key. */
    productTypeName: string;
    isAuthoritative: boolean;
  }[];
  unknownSuppliers: PimSupplierProductArticleNumberInput[];
};

/**
 * Splits a bulk add's per-article verdicts into the three outcomes the user has to be told
 * apart. A conflict is not a generic failure: it means PIM disagrees with the user about what
 * the article *is*, and it names the classification that blocked it — which is the only thing
 * that makes the rejection actionable.
 */
export function summariseAddProductPreferenceResults(
  results: readonly AddProductPreferenceResultFragment[]
): AddProductPreferenceSummary {
  const summary: AddProductPreferenceSummary = {
    added: [],
    conflicts: [],
    unknownSuppliers: [],
  };

  for (const result of results) {
    const { supplierId, articleNumber } = result.articleNumberIdentifier;
    switch (result.status) {
      case "ADDED":
        summary.added.push({ supplierId, articleNumber });
        break;
      case "PRODUCT_TYPE_CONFLICT":
        summary.conflicts.push({
          supplierId,
          articleNumber,
          productTypeKey: result.conflictingProductType?.productTypeKey ?? "",
          productTypeName:
            result.conflictingProductType?.label ??
            result.conflictingProductType?.productTypeKey ??
            "",
          isAuthoritative:
            result.conflictingProductType?.source === "AUTHORITATIVE",
        });
        break;
      case "UNKNOWN_SUPPLIER":
        summary.unknownSuppliers.push({ supplierId, articleNumber });
        break;
    }
  }

  return summary;
}

export function isPartialSuccess(
  summary: AddProductPreferenceSummary
): boolean {
  return (
    summary.added.length > 0 &&
    summary.conflicts.length + summary.unknownSuppliers.length > 0
  );
}

/** The four groups of a ranked preference list, in the order search ranks them. */
export const PREFERENCE_GROUPS = [
  "USER_ARTICLES",
  "ORGANISATION_ARTICLES",
  "USER_BRANDS",
  "ORGANISATION_BRANDS",
] as const;

export type PreferenceGroup = (typeof PREFERENCE_GROUPS)[number];

/** The two kinds the preferences screen shows on separate tabs. */
export type PreferenceKind = "ARTICLES" | "BRANDS";

export function preferenceKindOf(group: PreferenceGroup): PreferenceKind {
  return group === "USER_ARTICLES" || group === "ORGANISATION_ARTICLES"
    ? "ARTICLES"
    : "BRANDS";
}

type GroupableItem = {
  __typename: "PimArticlePreferenceItem" | "PimBrandPreferenceItem";
  scope: PimProductPreferenceScope;
};

export function preferenceGroupOf(item: GroupableItem): PreferenceGroup {
  const isArticle = item.__typename === "PimArticlePreferenceItem";
  if (item.scope === "USER") return isArticle ? "USER_ARTICLES" : "USER_BRANDS";
  return isArticle ? "ORGANISATION_ARTICLES" : "ORGANISATION_BRANDS";
}

export type PositionedPreferenceItem<I> = {
  item: I;
  group: PreferenceGroup;
  /** 0-based position within the group. */
  index: number;
  groupSize: number;
};

/** Keeps PIM's order, which is already grouped; a group is the same kind at the same scope. */
export function positionPreferenceItems<I extends GroupableItem>(
  items: readonly I[]
): PositionedPreferenceItem<I>[] {
  const groups = items.map(preferenceGroupOf);
  return items.map((item, position) => {
    const group = preferenceGroupOf(item);
    return {
      item,
      group,
      index: groups.slice(0, position).filter(other => other === group).length,
      groupSize: groups.filter(other => other === group).length,
    };
  });
}

export type PreferenceMove =
  | { kind: "top" }
  | { kind: "up" }
  | { kind: "down" }
  /** 1-based, as shown. */
  | { kind: "position"; position: number };

/** Mirrors the backend's cap on a reorder head, which names every item up to the moved one. */
export const PREFERENCE_REORDER_LIMIT = 1000;

/** The index a move lands on within its group, or `null` when it leaves the group or stays. */
export function moveTargetIndex(
  move: PreferenceMove,
  index: number,
  groupSize: number
): number | null {
  const target =
    move.kind === "top"
      ? 0
      : move.kind === "up"
        ? index - 1
        : move.kind === "down"
          ? index + 1
          : move.position - 1;
  return target >= 0 &&
    target < groupSize &&
    target < PREFERENCE_REORDER_LIMIT &&
    target !== index
    ? target
    : null;
}

/**
 * The reorder head: the group's new order up to and including the moved item. PIM ranks the
 * group's others after it in their current order, so a move to the top names the item alone.
 */
export function buildMoveHead<T>(
  groupOrder: readonly T[],
  index: number,
  targetIndex: number
): T[] {
  const moved = groupOrder[index];
  if (moved === undefined) return [];
  const rest = groupOrder.filter((_, position) => position !== index);
  return [...rest.slice(0, targetIndex), moved];
}

export function moveAvailability(
  index: number,
  groupSize: number,
  mayWrite: boolean
): { top: boolean; up: boolean; down: boolean; position: boolean } {
  const movable = mayWrite && groupSize > 1;
  return {
    top: movable && index > 0,
    up: movable && index > 0 && index - 1 < PREFERENCE_REORDER_LIMIT,
    down:
      movable && index < groupSize - 1 && index + 1 < PREFERENCE_REORDER_LIMIT,
    position: movable,
  };
}

const BRAND_LINE_SUMMARY_LIMIT = 3;

export type BrandLineSummary =
  | { kind: "wholeBrand" }
  | { kind: "lines"; shown: string[]; moreCount: number };

export function brandLineSummary(
  lines: readonly { title: string }[]
): BrandLineSummary {
  if (lines.length === 0) return { kind: "wholeBrand" };
  return {
    kind: "lines",
    shown: lines.slice(0, BRAND_LINE_SUMMARY_LIMIT).map(line => line.title),
    moreCount: Math.max(0, lines.length - BRAND_LINE_SUMMARY_LIMIT),
  };
}
